"use client";

import { useState } from "react";
import { Clock, Play, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { ClearButton } from "@/components/tools/clear-button";
import { CopyButton } from "@/components/tools/copy-button";
import { ErrorMessage } from "@/components/tools/error-message";
import { InputOutputPanel } from "@/components/tools/input-output-panel";
import { CodeOutput } from "@/components/tools/tool-output";
import {
  formatLocalTimestamp,
  parseDateTime,
  parseUnixTimestamp,
  type TimestampDetails,
  type TimestampInputUnit,
  type TimestampUnit,
} from "@/lib/tools/timestamp";

type TimestampMode = "unix" | "date";
type TimestampOutput = TimestampDetails & {
  detectedUnit?: TimestampUnit;
};

function formatTimestampCopyValue(details: TimestampOutput, localTime: string) {
  return [
    `Unix seconds: ${details.seconds}`,
    `Unix milliseconds: ${details.milliseconds}`,
    `ISO 8601: ${details.iso}`,
    `UTC: ${details.utc}`,
    `Local time: ${localTime}`,
    details.detectedUnit ? `Detected unit: ${details.detectedUnit}` : "",
  ]
    .filter(Boolean)
    .join("\n");
}

function getLocalTime(milliseconds: number) {
  return formatLocalTimestamp(milliseconds);
}

function TimestampResultTable({ details }: { details: TimestampOutput }) {
  const fields: Array<[string, string]> = [
    ["Unix seconds", String(details.seconds)],
    ["Unix milliseconds", String(details.milliseconds)],
    ["ISO 8601", details.iso],
    ["UTC", details.utc],
    ["Local time", getLocalTime(details.milliseconds)],
    ["Time zone", Intl.DateTimeFormat().resolvedOptions().timeZone || "Browser local"],
  ];

  if (details.detectedUnit) {
    fields.push(["Detected unit", details.detectedUnit]);
  }

  return (
    <div className="overflow-hidden rounded-md border border-zinc-200">
      <table className="w-full text-left text-sm">
        <tbody>
          {fields.map(([label, value]) => (
            <tr key={label} className="border-b border-zinc-100 last:border-0">
              <th className="w-40 bg-zinc-50 px-3 py-2 font-medium text-zinc-600">{label}</th>
              <td className="break-all px-3 py-2 font-mono text-zinc-900">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TimestampConverterTool() {
  const [mode, setMode] = useState<TimestampMode>("unix");
  const [unit, setUnit] = useState<TimestampInputUnit>("auto");
  const [input, setInput] = useState("");
  const [details, setDetails] = useState<TimestampOutput | null>(null);
  const [error, setError] = useState("");
  const copyValue = details
    ? formatTimestampCopyValue(details, getLocalTime(details.milliseconds))
    : "";

  const resetOutput = () => {
    setDetails(null);
    setError("");
  };

  const process = () => {
    const result = mode === "unix" ? parseUnixTimestamp(input, unit) : parseDateTime(input);
    setDetails(result.ok ? result.value : null);
    setError(result.ok ? "" : result.error);
  };

  const setExample = () => {
    setInput(mode === "unix" ? "1700000000" : "2023-11-14T22:13:20.000Z");
    resetOutput();
  };

  const setNow = () => {
    const now = new Date();
    setInput(
      mode === "unix" && unit === "milliseconds"
        ? String(now.getTime())
        : mode === "unix"
          ? String(Math.floor(now.getTime() / 1000))
          : now.toISOString(),
    );
    resetOutput();
  };

  return (
    <InputOutputPanel
      inputTitle={mode === "unix" ? "Unix timestamp" : "Date and time"}
      outputTitle="Converted time"
      inputActions={
        <>
          <div className="inline-flex rounded-md border border-zinc-200 bg-white p-1">
            {(["unix", "date"] as const).map((option) => (
              <Button
                key={option}
                type="button"
                size="sm"
                variant={mode === option ? "default" : "ghost"}
                onClick={() => {
                  setMode(option);
                  setInput("");
                  resetOutput();
                }}
              >
                {option === "unix" ? "Unix" : "Date"}
              </Button>
            ))}
          </div>
          <Button type="button" variant="secondary" size="sm" onClick={setNow}>
            <Clock className="h-4 w-4" />
            Now
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={setExample}>
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton
            onClear={() => {
              setInput("");
              setUnit("auto");
              resetOutput();
            }}
          />
        </>
      }
      outputActions={<CopyButton value={copyValue} />}
      input={
        <div className="max-w-xl space-y-4">
          <div className="space-y-2">
            <Label htmlFor="timestamp-input">
              {mode === "unix" ? "Timestamp" : "Date or ISO string"}
            </Label>
            <Input
              id="timestamp-input"
              value={input}
              onChange={(event) => {
                setInput(event.target.value);
                resetOutput();
              }}
              placeholder={
                mode === "unix" ? "1700000000 or 1700000000000" : "2023-11-14T22:13:20Z"
              }
            />
          </div>
          {mode === "unix" ? (
            <div className="max-w-xs space-y-2">
              <Label htmlFor="timestamp-unit">Unit</Label>
              <Select
                id="timestamp-unit"
                value={unit}
                onChange={(event) => {
                  setUnit(event.target.value as TimestampInputUnit);
                  resetOutput();
                }}
              >
                <option value="auto">Auto-detect</option>
                <option value="seconds">Seconds</option>
                <option value="milliseconds">Milliseconds</option>
              </Select>
            </div>
          ) : null}
          <ErrorMessage message={error} />
          <Button type="button" onClick={process}>
            <Play className="h-4 w-4" />
            Convert time
          </Button>
        </div>
      }
      output={
        details ? (
          <TimestampResultTable details={details} />
        ) : (
          <CodeOutput placeholder="Converted timestamps and date values will appear here." />
        )
      }
    />
  );
}
